import { useState, useRef } from 'react';
import type { Translate } from 'next-translate';
import { useAttributeTruncation } from '#hooks/useAttributeTruncation';
import { AttributeLink } from '#utils/mediaAttributes';

type TruncatableAttributeProps = {
  values: string[];
  t: Translate;
  isSearchable?: boolean;
};

const TruncatableAttribute = ({
  values,
  t,
  isSearchable,
}: TruncatableAttributeProps) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const cutoff = useAttributeTruncation(measureRef, values.length);

  const [expanded, setExpanded] = useState(false);

  const needsTruncation = cutoff < values.length;
  const visible =
    needsTruncation && !expanded ? values.slice(0, cutoff) : values;

  const searchUrl = `/${t('search').trim().toLowerCase()}`;

  return (
    <span className="tw-relative tw-block tw-w-full">
      {/* Hidden clone for measurement */}
      <span
        ref={measureRef}
        aria-hidden="true"
        className="tw-absolute tw-invisible tw-top-0 tw-left-0 tw-w-full tw-pointer-events-none"
      >
        {values.map((value, i) => (
          <span key={i} data-m={i}>
            {i > 0 && ', '}
            {value}
          </span>
        ))}
        <span data-more>
          … <span className="tw-inline">{t('attributeListShowMore')}</span>
        </span>
      </span>

      {visible.map((value, i) => (
        <span key={i}>
          {i > 0 && ', '}
          {isSearchable ? (
            <AttributeLink href={`${searchUrl}/${encodeURIComponent(value)}`}>
              {value}
            </AttributeLink>
          ) : (
            <>{value}</>
          )}
        </span>
      ))}

      {needsTruncation && (
        <>
          {!expanded ? '… ' : ' '}
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="tw-inline tw-underline tw-text-[var(--color-link)] hover:tw-text-[var(--color-link-hover)] tw-transition-colors tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
          >
            {expanded ? t('attributeListShowLess') : t('attributeListShowMore')}
          </button>
        </>
      )}
    </span>
  );
};

export default TruncatableAttribute;
